import { and, coalesce, eq, type InitialQueryBuilder, isNull, type QueryResult, type Ref } from "@tanstack/db";

import type { DirectWorkspaceInvitation } from "@storyboard/web-client";
import { labelInvitation } from "@/models/demoLabels";
import { store } from "@/store/store";
import type { DirectWsInvitation } from "@/store/types";

export async function bulkUpsertDirectWsInvitation(records: DirectWorkspaceInvitation[]) {
  if (records.length === 0) return;
  const data = records.map(
    (r): DirectWsInvitation =>
      labelInvitation({
        id: r.id,
        workspaceMembershipId: r.workspaceMembershipId,
        email: r.email ?? null,
        phoneNumber: r.phoneNumber ?? null,
        name: r.name ?? null,
        claimedAt: r.claimedAt ?? null,
        claimedBy: r.claimedBy ?? null,
        createdAt: r.createdAt ?? null,
        updatedAt: r.updatedAt ?? null,
      }),
  );
  // The old ON CONFLICT SET kept the first-seen createdAt.
  store.upsert("directWsInvitation", data, (existing, next) => ({
    ...next,
    createdAt: existing.createdAt,
  }));
}

export const currentFeedPendingInvitesQuery = (currentFeedId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ permission: store.collection("permission") })
    .innerJoin({ membership: store.collection("workspaceMembership") }, ({ permission, membership }) =>
      eq(permission.workspaceMembershipId, membership.id),
    )
    .innerJoin({ invitation: store.collection("directWsInvitation") }, ({ membership, invitation }) =>
      eq(membership.id, invitation.workspaceMembershipId),
    )
    .where(({ permission, membership, invitation }) =>
      and(
        eq(permission.feedId, currentFeedId),
        eq(permission.name, "read"),
        eq(permission.enabled, true),
        eq(membership.status, "active"),
        isNull(invitation.claimedBy),
      ),
    )
    // TanStack DB 0.12 types a whole-source select as `T | { [K]?: never }`; the annotation keeps the row type `T`.
    .select(({ invitation }): Ref<DirectWsInvitation> => invitation);

/** Unclaimed invitations of the workspace's active memberships — the invite count is the row count. */
export const workspaceInvitesQuery = (workspaceId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ invitation: store.collection("directWsInvitation") })
    .innerJoin({ membership: store.collection("workspaceMembership") }, ({ invitation, membership }) =>
      eq(invitation.workspaceMembershipId, membership.id),
    )
    .where(({ invitation, membership }) =>
      and(isNull(invitation.claimedBy), eq(membership.workspaceId, workspaceId), eq(membership.status, "active")),
    );

export const workspaceInviteTableQuery = (workspaceId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ invitation: store.collection("directWsInvitation") })
    .innerJoin({ membership: store.collection("workspaceMembership") }, ({ invitation, membership }) =>
      eq(invitation.workspaceMembershipId, membership.id),
    )
    .where(({ invitation, membership }) =>
      and(isNull(invitation.claimedBy), eq(membership.workspaceId, workspaceId), eq(membership.status, "active")),
    )
    .select(({ invitation, membership }) => ({
      id: invitation.id,
      workspaceMembershipId: invitation.workspaceMembershipId,
      email: invitation.email,
      phoneNumber: invitation.phoneNumber,
      name: invitation.name,
      claimedAt: invitation.claimedAt,
      claimedBy: invitation.claimedBy,
      createdAt: invitation.createdAt,
      updatedAt: invitation.updatedAt,
      contact: coalesce(invitation.email, invitation.phoneNumber),
      date: invitation.createdAt,
      membershipStatus: membership.status,
      role: membership.role,
    }))
    .orderBy(({ invitation }) => invitation.createdAt, { direction: "desc", nulls: "first" });

/** One row of {@link workspaceInviteTableQuery}, derived from its projection so the two cannot drift apart. */
export type WorkspaceInviteTableRow = QueryResult<ReturnType<ReturnType<typeof workspaceInviteTableQuery>>>;
